/**
 * ============================================================================
 * UTILITY HELPERS
 * ============================================================================
 *
 * Common utility functions used throughout the application.
 *
 * ============================================================================
 */

/**
 * DOM ready function
 * @param {Function} callback - Function to execute when DOM is ready
 */
function ready(callback) {
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', callback);
  } else {
    callback();
  }
}

/**
 * Debounce function
 * @param {Function} func - Function to debounce
 * @param {number} wait - Wait time in milliseconds
 * @param {boolean} immediate - Execute immediately
 * @returns {Function} Debounced function
 */
function debounce(func, wait, immediate = false) {
  let timeout;
  return function executedFunction(...args) {
    const later = () => {
      timeout = null;
      if (!immediate) func(...args);
    };
    const callNow = immediate && !timeout;
    clearTimeout(timeout);
    timeout = setTimeout(later, wait);
    if (callNow) func(...args);
  };
}

/**
 * Throttle function
 * @param {Function} func - Function to throttle
 * @param {number} limit - Limit in milliseconds
 * @returns {Function} Throttled function
 */
function throttle(func, limit) {
  let inThrottle;
  return function executedFunction(...args) {
    if (!inThrottle) {
      func.apply(this, args);
      inThrottle = true;
      setTimeout(() => inThrottle = false, limit);
    }
  };
}

/**
 * Get element by selector with optional context
 * @param {string} selector - CSS selector
 * @param {Element} context - Context element (default: document)
 * @returns {Element|null} Found element
 */
function $(selector, context = document) {
  return context.querySelector(selector);
}

/**
 * Get elements by selector with optional context
 * @param {string} selector - CSS selector
 * @param {Element} context - Context element (default: document)
 * @returns {NodeList} Found elements
 */
function $$(selector, context = document) {
  return context.querySelectorAll(selector);
}

/**
 * Add event listener with optional delegation
 * @param {Element|string} target - Target element or selector
 * @param {string} event - Event type
 * @param {Function} handler - Event handler
 * @param {string} delegate - Delegation selector
 */
function on(target, event, handler, delegate) {
  const element = typeof target === 'string' ? $(target) : target;

  if (!element) return;

  if (delegate) {
    element.addEventListener(event, (e) => {
      if (e.target.matches(delegate)) {
        handler.call(e.target, e);
      }
    });
  } else {
    element.addEventListener(event, handler);
  }
}

/**
 * Remove event listener
 * @param {Element|string} target - Target element or selector
 * @param {string} event - Event type
 * @param {Function} handler - Event handler
 */
function off(target, event, handler) {
  const element = typeof target === 'string' ? $(target) : target;
  if (element) {
    element.removeEventListener(event, handler);
  }
}

/**
 * Check if element matches selector
 * @param {Element} element - Element to check
 * @param {string} selector - CSS selector
 * @returns {boolean} True if matches
 */
function matches(element, selector) {
  return element.matches(selector);
}

/**
 * Get closest ancestor matching selector
 * @param {Element} element - Starting element
 * @param {string} selector - CSS selector
 * @returns {Element|null} Closest matching ancestor
 */
function closest(element, selector) {
  return element.closest(selector);
}

/**
 * Add class to element
 * @param {Element|string} target - Target element or selector
 * @param {string} className - Class name to add
 */
function addClass(target, className) {
  const element = typeof target === 'string' ? $(target) : target;
  if (element) {
    element.classList.add(className);
  }
}

/**
 * Remove class from element
 * @param {Element|string} target - Target element or selector
 * @param {string} className - Class name to remove
 */
function removeClass(target, className) {
  const element = typeof target === 'string' ? $(target) : target;
  if (element) {
    element.classList.remove(className);
  }
}

/**
 * Toggle class on element
 * @param {Element|string} target - Target element or selector
 * @param {string} className - Class name to toggle
 * @returns {boolean} True if class was added
 */
function toggleClass(target, className) {
  const element = typeof target === 'string' ? $(target) : target;
  if (element) {
    return element.classList.toggle(className);
  }
  return false;
}

/**
 * Check if element has class
 * @param {Element|string} target - Target element or selector
 * @param {string} className - Class name to check
 * @returns {boolean} True if has class
 */
function hasClass(target, className) {
  const element = typeof target === 'string' ? $(target) : target;
  return element ? element.classList.contains(className) : false;
}

/**
 * Get/set element attribute
 * @param {Element|string} target - Target element or selector
 * @param {string} name - Attribute name
 * @param {string} value - Attribute value (optional)
 * @returns {string|null} Attribute value if getting
 */
function attr(target, name, value) {
  const element = typeof target === 'string' ? $(target) : target;
  if (!element) return null;

  if (value !== undefined) {
    element.setAttribute(name, value);
  } else {
    return element.getAttribute(name);
  }
}

/**
 * Remove attribute from element
 * @param {Element|string} target - Target element or selector
 * @param {string} name - Attribute name
 */
function removeAttr(target, name) {
  const element = typeof target === 'string' ? $(target) : target;
  if (element) {
    element.removeAttribute(name);
  }
}

/**
 * Get/set element data attribute
 * @param {Element|string} target - Target element or selector
 * @param {string} name - Data attribute name (without data- prefix)
 * @param {string} value - Data attribute value (optional)
 * @returns {string|null} Data attribute value if getting
 */
function data(target, name, value) {
  return attr(target, `data-${name}`, value);
}

/**
 * Create element with optional attributes and content
 * @param {string} tag - HTML tag name
 * @param {Object} attributes - Element attributes
 * @param {string|Element} content - Element content
 * @returns {Element} Created element
 */
function createElement(tag, attributes = {}, content = '') {
  const element = document.createElement(tag);

  Object.entries(attributes).forEach(([key, value]) => {
    element.setAttribute(key, value);
  });

  if (typeof content === 'string') {
    element.textContent = content;
  } else if (content instanceof Element) {
    element.appendChild(content);
  }

  return element;
}

/**
 * Check if device is mobile
 * @returns {boolean} True if mobile
 */
function isMobile() {
  return window.innerWidth < 768;
}

/**
 * Check if device is tablet
 * @returns {boolean} True if tablet
 */
function isTablet() {
  return window.innerWidth >= 768 && window.innerWidth < 1024;
}

/**
 * Check if device is desktop
 * @returns {boolean} True if desktop
 */
function isDesktop() {
  return window.innerWidth >= 1024;
}

/**
 * Get viewport dimensions
 * @returns {Object} Viewport width and height
 */
function getViewport() {
  return {
    width: window.innerWidth,
    height: window.innerHeight
  };
}

/**
 * Scroll to element smoothly using scrollIntoView
 * @param {Element|string} target - Target element or selector
 * @param {Object} options - Scroll options
 */
function scrollToElement(target, options = {}) {
  const element = typeof target === 'string' ? $(target) : target;
  if (element) {
    element.scrollIntoView({
      behavior: 'smooth',
      block: 'start',
      ...options
    });
  }
}

/**
 * Wait for element to be stable (no layout shifts)
 * @param {Element} element - Element to observe
 * @param {number} timeout - Maximum wait time in ms (default: 3000)
 * @returns {Promise} Promise that resolves when element is stable
 */
function waitForElementStability(element, timeout = 3000) {
  return new Promise((resolve) => {
    let lastHeight = element.offsetHeight;
    let lastTop = element.getBoundingClientRect().top;
    let stableCount = 0;
    const requiredStableChecks = 3; // Element muss 3x stabil sein
    const checkInterval = 100; // Alle 100ms prüfen
    let totalWaitTime = 0;

    const checkStability = setInterval(() => {
      const currentHeight = element.offsetHeight;
      const currentTop = element.getBoundingClientRect().top;

      // Prüfe ob Position und Höhe stabil sind
      if (currentHeight === lastHeight && Math.abs(currentTop - lastTop) < 1) {
        stableCount++;

        if (stableCount >= requiredStableChecks) {
          clearInterval(checkStability);
          resolve(true);
        }
      } else {
        // Element hat sich verändert, Counter zurücksetzen
        stableCount = 0;
        lastHeight = currentHeight;
        lastTop = currentTop;
      }

      totalWaitTime += checkInterval;

      // Timeout erreicht
      if (totalWaitTime >= timeout) {
        clearInterval(checkStability);
        console.warn('waitForElementStability: Timeout reached, proceeding anyway');
        resolve(false);
      }
    }, checkInterval);
  });
}

/**
 * Scroll to element smoothly with offset
 * @param {Element|string} target - Target element or selector
 * @param {number} offset - Offset in pixels (negative = scroll up, positive = scroll down)
 * @param {Object} options - Additional options
 * @param {boolean} options.updateHash - Update URL hash (default: false)
 * @param {boolean} options.focus - Focus element after scroll (default: true)
 * @param {boolean} options.waitForStability - Wait for element to be stable (default: true)
 * @param {number} options.stabilityTimeout - Max wait time for stability in ms (default: 3000)
 * @returns {Promise} Promise that resolves when scroll is complete
 */
function scrollToWithOffset(target, offset = -20, options = {}) {
  return new Promise(async (resolve) => {
    const element = typeof target === 'string' ? $(target) : target;

    if (!element) {
      console.warn('scrollToWithOffset: Target element not found', target);
      resolve(false);
      return;
    }

    const {
      updateHash = false,
      focus = true,
      waitForStability = true,
      stabilityTimeout = 3000
    } = options;

    // Warte auf Element-Stabilität (wichtig für Third-Party-Content)
    if (waitForStability) {
      console.log('⏳ Waiting for element stability before scrolling...');
      await waitForElementStability(element, stabilityTimeout);
      console.log('✅ Element is stable, scrolling now');
    }

    // Calculate target position with offset
    const elementPosition = element.getBoundingClientRect().top;
    const offsetPosition = elementPosition + window.pageYOffset + offset;

    // Smooth scroll to position
    window.scrollTo({
      top: offsetPosition,
      behavior: 'smooth'
    });

    // Wait for scroll to complete (approximate)
    setTimeout(() => {
      // Update URL hash if requested
      if (updateHash && element.id) {
        history.pushState(null, null, `#${element.id}`);
      }

      // Focus element for accessibility
      if (focus) {
        const originalTabIndex = element.getAttribute('tabindex');
        const hadTabIndex = element.hasAttribute('tabindex');

        // Make element focusable if it isn't already
        if (!hadTabIndex) {
          element.setAttribute('tabindex', '-1');
        }

        element.focus({ preventScroll: true });

        // Restore original tabindex state
        if (!hadTabIndex) {
          element.removeAttribute('tabindex');
        } else if (originalTabIndex !== null) {
          element.setAttribute('tabindex', originalTabIndex);
        }
      }

      resolve(true);
    }, 600); // Approximate scroll duration
  });
}

/**
 * Initialize smooth anchor scrolling for all hash links
 * @param {Object} config - Configuration options
 * @param {number} config.offset - Scroll offset in pixels (default: -20)
 * @param {string} config.selector - Link selector (default: 'a[href*="#"]')
 * @param {boolean} config.updateHash - Update URL hash (default: true)
 * @param {number} config.initialLoadDelay - Delay before scrolling on page load in ms (default: 500)
 * @param {number} config.stabilityTimeout - Max wait time for element stability in ms (default: 3000)
 * @param {Function} config.onScroll - Callback after scroll
 */
function initSmoothAnchorScrolling(config = {}) {
  const {
    offset = -20,
    selector = 'a[href*="#"]',
    updateHash = true,
    initialLoadDelay = 500,
    stabilityTimeout = 3000,
    onScroll = null
  } = config;

  // Handle clicks on anchor links
  document.addEventListener('click', (e) => {
    const link = e.target.closest(selector);

    if (!link) return;

    const href = link.getAttribute('href');
    if (!href || href === '#') return;

    // Parse the href
    const url = new URL(href, window.location.href);

    // Only handle same-page anchors
    if (url.pathname !== window.location.pathname) return;
    if (!url.hash) return;

    // Get target element
    const targetId = url.hash.slice(1);
    const targetElement = document.getElementById(targetId);

    if (!targetElement) return;

    // Prevent default jump
    e.preventDefault();

    // Get custom offset from link if available
    const customOffset = link.getAttribute('data-scroll-offset');
    const scrollOffset = customOffset ? parseInt(customOffset, 10) : offset;

    // Scroll to element with stability check
    scrollToWithOffset(targetElement, scrollOffset, {
      updateHash: updateHash,
      focus: true,
      waitForStability: true,
      stabilityTimeout: stabilityTimeout
    }).then((success) => {
      if (success && onScroll) {
        onScroll(targetElement, link);
      }
    });
  });

  // Handle initial page load with hash
  // Check if hash was stored by inline script in theme.liquid
  const originalHash = window.__smoothScrollHash || window.location.hash;

  console.log('🔍 [INIT] Checking for hash...');
  console.log('🔍 [INIT] window.__smoothScrollHash:', window.__smoothScrollHash);
  console.log('🔍 [INIT] window.location.hash:', window.location.hash);
  console.log('🔍 [INIT] originalHash:', originalHash);

  if (originalHash) {
    const targetId = originalHash.slice(1);

    console.log('🔗 [INIT] Processing stored hash:', originalHash);
    console.log('🔗 [INIT] Target ID:', targetId);

    ready(() => {
      console.log('📄 [READY] DOM is ready, looking for element:', targetId);
      const targetElement = document.getElementById(targetId);

      if (targetElement) {
        console.log('✅ [READY] Target element found:', targetElement);

        // Wait for window.load event (all resources loaded)
        const scrollToTarget = () => {
          console.log('🌐 [LOAD] Window loaded, waiting', initialLoadDelay, 'ms before scrolling');

          // Additional delay to allow third-party scripts to initialize
          setTimeout(() => {
            console.log('📍 [SCROLL] Starting scroll to target element:', targetId);

            // Restore scroll-behavior to smooth
            document.documentElement.style.scrollBehavior = '';

            scrollToWithOffset(targetElement, offset, {
              updateHash: false, // Wir setzen Hash manuell später
              focus: true,
              waitForStability: true,
              stabilityTimeout: stabilityTimeout
            }).then((success) => {
              if (success) {
                // Setze Hash zurück nach erfolgreichem Scroll
                history.replaceState(null, null, originalHash);
                console.log('✅ [COMPLETE] Scroll complete, hash restored:', originalHash);
              } else {
                console.warn('⚠️ [COMPLETE] Scroll completed with warnings');
              }

              // Clean up
              delete window.__smoothScrollHash;
              delete window.__smoothScrollPrevented;
            });
          }, initialLoadDelay);
        };

        // If page is already loaded, scroll immediately
        if (document.readyState === 'complete') {
          console.log('⚡ [READY] Page already loaded, scrolling now');
          scrollToTarget();
        } else {
          console.log('⏳ [READY] Waiting for window.load event');
          // Otherwise wait for full page load
          window.addEventListener('load', scrollToTarget);
        }
      } else {
        // Element nicht gefunden, Hash zurücksetzen falls vorhanden
        console.error('❌ [READY] Target element not found:', targetId);
        console.log('🔍 [DEBUG] Available elements with IDs:',
          Array.from(document.querySelectorAll('[id]')).map(el => el.id).slice(0, 20)
        );

        if (originalHash) {
          history.replaceState(null, null, originalHash);
        }
        document.documentElement.style.scrollBehavior = '';
        delete window.__smoothScrollHash;
        delete window.__smoothScrollPrevented;
      }
    });
  } else {
    console.log('ℹ️ [INIT] No hash to process');
  }

  console.log('✨ Smooth anchor scrolling initialized with offset:', offset);
}

/**
 * Format money using Shopify's money format
 * @param {number} cents - Amount in cents
 * @param {string} format - Money format string
 * @returns {string} Formatted money string
 */
function formatMoney(cents, format = '${{amount}}') {
  if (typeof cents === 'string') {
    cents = cents.replace('.', '');
  }

  const value = (cents / 100).toFixed(2);

  return format
    .replace('{{amount}}', value)
    .replace('{{amount_no_decimals}}', Math.floor(cents / 100))
    .replace('{{amount_with_comma_separator}}', value.replace('.', ','));
}
